import { useCallback, useEffect, useMemo } from "react";
import { Center } from "@mantine/core";
import { useForm } from "@mantine/form";
import { TFunction } from "i18next";
import { useSelector } from "react-redux";

import { Button, Modal, NumberInput, Select, Text } from "components";
import { ServiceConstants } from "constant";
import { CreditService } from "services";
import { CreditOptionsResponse, CreditRequestPaymentRequest } from "types";
import { AppUtils } from "utils";

import { IRootState } from "config/redux";

const { REQUEST_CREDIT_PAYMENT_CREATE_ID, REQUEST_PENDING } = ServiceConstants;

interface CreditPaymentCreateModalProps {
  options: CreditOptionsResponse;
  creditId: number;
  opened: boolean;
  onClose: () => void;
  t: TFunction;
}

const CreditPaymentCreateModal = ({ options, creditId, opened, onClose, t }: CreditPaymentCreateModalProps) => {
  const createServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_PAYMENT_CREATE_ID]);

  useEffect(() => {
    if (!opened) {
      form.setValues({
        requestId: creditId,
        type: undefined,
        bank: undefined,
        amount: undefined
      });
    }
  }, [opened]);

  const createPayment = useCallback(() => CreditService.creditPaymentCreate(form.getTransformedValues()), [creditId]);
  const isLoading = useMemo(() => createServiceObject && createServiceObject.status === REQUEST_PENDING, [createServiceObject]);

  const form = useForm({
    initialValues: {} as CreditRequestPaymentRequest,
    validate: {
      type: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      amount: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null
    },
  });

  const renderBankList = useCallback(() => {
    const type = form.getTransformedValues().type;
    console.log(options.structureTypeBanks);
    if (!type) {
      return [];
    }
    return options.structureTypeBanks[type] ?? [];
  }, []);

  return (
    <Modal opened={opened} onClose={onClose} title={t('credit.payment.new.title')}>
      <Select
        label={t('credit.payment.type.label')}
        data={[
          { value: 'PAYMENT', label: t('credit.payment.type.options.PAYMENT') },
          { value: 'CREDIT_COVERAGE', label: t('credit.payment.type.options.CREDIT_COVERAGE') },
          { value: 'CARD_COVERAGE', label: t('credit.payment.type.options.CARD_COVERAGE') },
          { value: 'LEASING_COVERAGE', label: t('credit.payment.type.options.LEASING_COVERAGE') },
        ]}
        key={form.key('type')}
        {...form.getInputProps('type')}
        required={true}
        mt={'sm'}
      />
      <Select
        label={t('credit.bank.label')}
        data={renderBankList()?.map((el: string) => (
          { value: el, label: t(`credit.bank.options.${el}`) }
        ))}
        key={form.key('bank')}
        {...form.getInputProps('bank')}
        mt={'sm'}
      />
      <NumberInput
          label={t('credit.payment.amount.label')}
          key={form.key('amount')}
          {...form.getInputProps('amount')}
          allowDecimal={true}
          decimalScale={2}
          fixedDecimalScale={true}
          required={true}
          mt={'sm'}
        />
      <Center mt={32}>
        <Button
          onClick={createPayment}
          disabled={isLoading || !form.isDirty() || !form.isValid()}
          loading={isLoading}
        >
          <Text>{t('action.submit')}</Text>
        </Button>
      </Center>
    </Modal>
  )
}

export default CreditPaymentCreateModal;